package components.accounting

import components.userName
import components.project.LocalStorageKeys
import components.project.ServerProjectPreviewsTable
import components.project.ServerProjectPreviewsTable.projectDetails
import components.project.projectsFiltering
import it.neckar.commons.kotlin.js.LocalStorageKey
import it.neckar.commons.kotlin.js.LocalStorageKeyPrefix
import it.neckar.commons.kotlin.js.LocalStorageSupport
import it.neckar.commons.kotlin.js.LocalStorageSupport.saveToLocalStorageOptional
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.company.CompanyCode
import it.neckar.lizergy.model.company.CompanyProfile
import it.neckar.lizergy.model.company.CompanyResolver
import it.neckar.lizergy.model.company.MainCompanyProfile
import it.neckar.lizergy.model.company.NeckarITCompanyProfile
import it.neckar.lizergy.model.company.PartnerCompanyProfile
import it.neckar.lizergy.model.company.TestCompanyProfile
import it.neckar.lizergy.model.company.user.UserInformation
import it.neckar.lizergy.model.financial.currency.Money
import it.neckar.lizergy.model.price.AccountingStatus
import it.neckar.lizergy.model.price.ResolvedEarningsEntry
import it.neckar.lizergy.model.project.previews.AccountingProjectPreview
import it.neckar.lizergy.model.project.previews.AccountingResolvedConfigurationPreview
import it.neckar.lizergy.model.project.previews.AccountingResolvedProjectPreview
import it.neckar.lizergy.model.project.previews.ProjectQueryForUser
import it.neckar.lizergy.model.project.process.state.LizergyProcessStates
import it.neckar.lizergy.model.project.process.state.LizergyProcessStatesBrowserSerializer
import it.neckar.lizergy.model.project.process.state.ProjectProcessStateEntry.ProjectProcessStates
import it.neckar.open.collections.fastForEach
import it.neckar.open.concurrent.debounce
import it.neckar.open.kotlin.lang.divFloor
import it.neckar.react.common.*
import it.neckar.react.common.FontAwesome.faCircleCheck
import it.neckar.react.common.form.*
import it.neckar.react.common.router.*
import it.neckar.react.common.table.*
import it.neckar.user.UserLoginName
import kotlinx.html.TD
import kotlinx.serialization.builtins.serializer
import plannerI18nConfiguration
import react.*
import react.dom.*
import router.RouterUrls
import router.useDocumentTitle
import services.UiActions
import services.http.AccountingProjectQueryRequest
import services.http.AccountingQuerySelection
import services.http.ProjectQuerySortedBy
import services.http.ProjectQuerySorting
import store.hooks.useRequireCompanyForLoggedInUser
import store.hooks.useRequireLoggedInUser
import store.hooks.useSelectCompanyResolver

val AccountingOverview: FC<Props> = fc("AccountingOverview") {
  val navigate = useNavigateUrl()
  val loggedInUser = useRequireLoggedInUser()
  val companyResolver = useSelectCompanyResolver()
  val companyName = useRequireCompanyForLoggedInUser().simpleName

  useDocumentTitle(companyName, "Abrechnung")

  val keyPrefix = LocalStorageKeyPrefix("AccountingOverview")

  val filteredByCompanyCode: StateInstance<CompanyCode?> = useState(null)

  // If the value is null, all projects shall be shown.
  val filteredByMaintainerName: StateInstance<UserLoginName?> = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.maintainer}"),
      serializer = UserLoginName.serializer()
    )
  )

  // If the value is null, all projects shall be shown.
  val filteredByEditorName: StateInstance<UserLoginName?> = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.editor}"),
      serializer = UserLoginName.serializer()
    )
  )

  val filteredByProjectState: StateInstance<LizergyProcessStates?> = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.processState}"),
      serializer = LizergyProcessStatesBrowserSerializer
    )
  )

  val filterInputStateProject = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.filterProject}"),
      serializer = String.serializer()
    ) ?: ""
  )
  val filterInputStateAddress = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.filterAddress}"),
      serializer = String.serializer()
    ) ?: ""
  )

  val sortedByFunction = useState(
    SortedByFunction(
      sortedBy = SortedBy.SortedDescending,
      tableDataColumn = ServerProjectPreviewsTable.getMostRecentProcessStateColumn<AccountingResolvedProjectPreview>(),
    )
  )

  val accountingQuerySelection = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.accountingQuerySelection}"),
      serializer = AccountingQuerySelection.serializer()
    ) ?: AccountingQuerySelection.PendingForAccounting
  )
  val howDoYouLikeYourQuoteElements = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.howDoYouLikeYourQuoteElements}"),
      serializer = AccountingProjectPreview.QuoteElements.serializer()
    ) ?: AccountingProjectPreview.QuoteElements.Frozen
  )

  val pageSize: StateInstance<Int?> = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalTableStorageKeys.pageSize}"),
      serializer = Int.serializer()
    ) ?: 15
  )

  val currentPage = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalTableStorageKeys.currentPage}"),
      serializer = Int.serializer()
    ) ?: 0
  )

  val visibleProjects: StateInstance<List<AccountingResolvedProjectPreview>?> = useState(null)
  val numberOfFilteredProjects: StateInstance<Int?> = useState(null)
  val numberOfRelevantProjects: StateInstance<Int?> = useState(null)


  val updateFilteredProjects = useRef(
    debounce<AccountingProjectQueryRequest>(coroutineScope = AppScope) { request ->
      visibleProjects.setter(null)
      numberOfFilteredProjects.setter(null)
      numberOfRelevantProjects.setter(null)

      UiActions.accountingQueryProjectPreviews(request) { response ->
        val queryInformation = response?.data
        visibleProjects.setter(queryInformation?.visibleProjectPreviews)
        numberOfFilteredProjects.setter(queryInformation?.numberOfTotalFilteredProjects)
        numberOfRelevantProjects.setter(queryInformation?.numberOfTotalRelevantProjects)
        currentPage.setter { old ->
          val newCurrentPage = queryInformation?.numberOfTotalFilteredProjects?.divFloor(pageSize.value ?: 1)
          if (newCurrentPage == old) return@setter old
          old.coerceIn(0, newCurrentPage)
        }
      }
    }
  )

  useMemo(
    accountingQuerySelection.value,
    filteredByCompanyCode.value,
    filteredByMaintainerName.value,
    filteredByEditorName.value,
    filterInputStateProject.value,
    filterInputStateAddress.value,
    filteredByProjectState.value,
    pageSize.value,
    currentPage.value,
    sortedByFunction.value,
    howDoYouLikeYourQuoteElements.value,
  ) {
    val sorting = ProjectQuerySorting(
      sortedBy = when (sortedByFunction.value.sortedBy) {
        SortedBy.Unsorted -> ProjectQuerySortedBy.Unsorted
        SortedBy.SortedAscending -> ProjectQuerySortedBy.SortedAscending
        SortedBy.SortedDescending -> ProjectQuerySortedBy.SortedDescending
      },
      sortedColumn = ServerProjectPreviewsTable.parseTableColumnId(sortedByFunction.value.sortedColumn.id),
      projectQueryForUser = ProjectQueryForUser.Any,
      loggedInUser = loggedInUser.loginName,
    )

    val request = AccountingProjectQueryRequest(
      processStatesToFilter = LizergyProcessStates.allAccountingProcessStates,
      processStatesToHide = listOf(ProjectProcessStates.Archived, ProjectProcessStates.Paused),
      accountingQuerySelection = accountingQuerySelection.value,
      filteredByCompanyCode = filteredByCompanyCode.value,
      filteredByMaintainer = filteredByMaintainerName.value,
      filteredByEditor = filteredByEditorName.value,
      filterValueProject = filterInputStateProject.value,
      filterValueAddress = filterInputStateAddress.value,
      filterByProjectState = filteredByProjectState.value,
      indexOfFirstVisibleProject = pageSize.value?.let { currentPage.value * it } ?: 0,
      indexOfLastVisibleProject = pageSize.value?.let { (currentPage.value + 1) * it } ?: Int.MAX_VALUE,
      sorting = sorting,
      projectQueryComponent = null,
      howDoYouLikeYourQuoteElements = howDoYouLikeYourQuoteElements.value,
      loggedInUser = loggedInUser.loginName,
    )

    updateFilteredProjects.current?.let { it(request) }
  }


  div {

    h2 {
      +"Abrechnung"
    }

    div {

      projectsFiltering(
        filteredByCompanyCode = filteredByCompanyCode,
        filteredByMaintainerName = filteredByMaintainerName,
        filteredByEditorName = filteredByEditorName,
        filteredByProjectState = filteredByProjectState,
        filterInputStateProject = filterInputStateProject,
        filterInputStateAddress = filterInputStateAddress,
        processStatesToFilter = LizergyProcessStates.allAccountingProcessStates,
        localStorageKeyPrefix = keyPrefix,
      )

      div("row g-2 mt-2 mb-3") {
        div("col") {
          floatingSelectEnum(
            valueAndSetter = accountingQuerySelection,
            formatter = {
              when (it) {
                AccountingQuerySelection.All -> "ALLE"
                AccountingQuerySelection.PendingForAccounting -> "Ausstehend"
                AccountingQuerySelection.SelectedForAccounting -> "Gebucht"
                AccountingQuerySelection.Accounted -> "Abgerechnet"
              }
            },
            availableOptions = AccountingQuerySelection.entries,
            fieldName = "accountingQuerySelection",
            title = "Projektauswahl",
            additionalOnChange = { saveToLocalStorageOptional(LocalStorageKey("${keyPrefix}${LocalStorageKeys.accountingQuerySelection}"), it) },
          )
        }

        div("col") {
          floatingSelectEnum(
            valueAndSetter = howDoYouLikeYourQuoteElements,
            formatter = {
              when (it) {
                AccountingProjectPreview.QuoteElements.Frozen -> "Nur eingefrorene Preise"
                AccountingProjectPreview.QuoteElements.UpToDate -> "Nur aktuelle Preise"
                //AccountingProjectPreview.QuoteElements.Whatever -> "Aktuelle Preise, falls Eingefrorene nicht vorhanden"
              }
            },
            availableOptions = AccountingProjectPreview.QuoteElements.entries,
            fieldName = "howDoYouLikeYourQuoteElements",
            title = "Preise",
            additionalOnChange = { saveToLocalStorageOptional(LocalStorageKey("${keyPrefix}${LocalStorageKeys.howDoYouLikeYourQuoteElements}"), it) },
          )
        }
      }

      busyIfNull(visibleProjects.value) { loadedProjectPreviews ->
        remoteTableWithPagination(
          tableClasses = "table table-striped table-hover table-responsive",
          entries = loadedProjectPreviews,
          columns = buildList {
            add(ServerProjectPreviewsTable.editButtonFirstColumn(loggedInUser) { navigate(RouterUrls.project(it).configuration((it.currentConfigurationPreview ?: return@editButtonFirstColumn).configurationId).earnings) })
            add(selectedForAccountingColumn())
            add(getAccountingProjectColumn(companyResolver, loggedInUser))
            add(earningsTable(companyResolver, howDoYouLikeYourQuoteElements.value))
            add(ServerProjectPreviewsTable.getMostRecentProcessStateColumn())
          },
          pageSize = pageSize,
          currentPage = currentPage,
          numberOfTotalFilteredProjects = numberOfFilteredProjects.value ?: 0,
          sortedByFunction = sortedByFunction,
          keyPrefix = keyPrefix,
        )
      }

      div("mt-1") {
        span("form-text") {
          +"${numberOfFilteredProjects.value ?: "-"} von ${numberOfRelevantProjects.value ?: "-"} Projekten ausgewählt"
        }
      }

    }

  }

}


data class EarningsTableEntries(
  val companyProfile: CompanyProfile,
  val configurationPreview: AccountingResolvedConfigurationPreview,
  val earningsCompanyEntries: List<ResolvedEarningsEntry>,
) {
  val earningsCompanyEntriesByCompany: Map<CompanyCode, List<ResolvedEarningsEntry>>
    get() = earningsCompanyEntries.groupBy { it.company }

  val relevantEarningsCompanyEntries: List<ResolvedEarningsEntry>
    get() = when (companyProfile) {
      is MainCompanyProfile -> emptyList()
      NeckarITCompanyProfile, is PartnerCompanyProfile -> earningsCompanyEntriesByCompany[companyProfile.companyCode] ?: emptyList()
      is TestCompanyProfile -> emptyList()
    }

  val openEarningsCompanyEntries: List<ResolvedEarningsEntry>
    get() = relevantEarningsCompanyEntries.filter { it.accountingStatus == AccountingStatus.Pending }

  val accountedEarningsCompanyEntries: List<ResolvedEarningsEntry>
    get() = relevantEarningsCompanyEntries.filter { it.accountingStatus == AccountingStatus.Accounted }

  fun relevantEarnings(howDoYouLikeYourQuoteElements: AccountingProjectPreview.QuoteElements): Money {
    return when (companyProfile) {
      is MainCompanyProfile -> configurationPreview.getEarningsForMainCompany(howDoYouLikeYourQuoteElements)
      is PartnerCompanyProfile, is TestCompanyProfile -> configurationPreview.getEarningsForCompany(companyProfile.companyCode, howDoYouLikeYourQuoteElements)
      NeckarITCompanyProfile -> configurationPreview.getNeckarITEarnings(howDoYouLikeYourQuoteElements)
    }
  }
}


fun selectedForAccountingColumn(): TableHeaderColumn<AccountingResolvedProjectPreview> {
  return TableHeaderColumn(
    id = "selectedForAccounting",
    title = "Gebucht",
    sortFunction = compareBy { it.currentConfigurationPreview?.earningsDistribution?.selectedForAccounting },
  ) { projectPreview ->
    {
      attrs {
        addClass("mt-3")
        addClass("text-center")
      }

      val currentConfigurationPreview = projectPreview.currentConfigurationPreview
      if (currentConfigurationPreview != null) {
        SelectedForAccountingColumn {
          attrs {
            this.projectPreview = projectPreview
            this.currentConfigurationPreview = currentConfigurationPreview
          }
        }
      }
    }
  }
}

val SelectedForAccountingColumn: FC<SelectedForAccountingColumnProps> = fc("SelectedForAccountingColumn") { props ->
  val projectPreview = props::projectPreview.safeGet()
  val currentConfigurationPreview = props::currentConfigurationPreview.safeGet()

  val earningsDistribution = currentConfigurationPreview.earningsDistribution

  val selectedForAccounting = useState(earningsDistribution.selectedForAccounting)

  checkbox(
    value = selectedForAccounting.value,
    onChange = {
      selectedForAccounting.setter(it)
      UiActions.saveEarningsDistribution(projectId = projectPreview.projectId, configurationId = currentConfigurationPreview.configurationId, earningsDistribution = earningsDistribution.copy(selectedForAccounting = it))
    },
    fieldName = "${projectPreview.projectId}-selectedForAccounting",
    title = "",
  )
}

external interface SelectedForAccountingColumnProps : Props {
  var projectPreview: AccountingResolvedProjectPreview
  var currentConfigurationPreview: AccountingResolvedConfigurationPreview
}

fun getAccountingProjectColumn(companyResolver: CompanyResolver, loggedInUser: UserInformation): TableDataColumn<AccountingResolvedProjectPreview> {
  return TableDataColumn(
    id = "project",
    title = "Projekt",
    sortFunction = compareBy { it.displayName },
  ) { projectPreview ->
    {
      accountingProjectColumnDetails(projectPreview, companyResolver, loggedInUser)
    }
  }
}

fun RDOMBuilder<TD>.accountingProjectColumnDetails(projectPreview: AccountingResolvedProjectPreview, companyResolver: CompanyResolver, loggedInUser: UserInformation) {
  projectDetails(projectPreview, companyResolver)

  p("form-text") { +"Kunde: ${projectPreview.customer.companyOrCompleteName()}" }
  if (projectPreview.customer.address != projectPreview.location.address) {
    p("form-text") { +projectPreview.customer.address.singleLine() }
  }
  projectPreview.currentConfigurationPreview?.let { configurationPreview ->
    p("form-text") { +"Module: ${configurationPreview.modulesReport.countByModules.joinToString(", ") { "${it.count} × ${it.moduleType.description}" }}" }
    configurationPreview.batteryConfiguration?.let { batteryConfiguration ->
      p("form-text") { +"Batterie: ${batteryConfiguration.format()}" }
    }
    p("form-text") { +"Wechselrichter: ${configurationPreview.inverterSelection.format(plannerI18nConfiguration)}" }
  }
  p("form-text") {
    +"Ansprechpartner: "
    userName(projectPreview.maintainerInformation)
  }
}

fun earningsTable(companyResolver: CompanyResolver, howDoYouLikeYourQuoteElements: AccountingProjectPreview.QuoteElements): TableDataColumn<AccountingResolvedProjectPreview> {
  return TableDataColumn(
    id = "accountingTable",
    title = "Abrechnung",
    titleClasses = "justify-content-between col-lg-4",
    sortFunction = compareBy { it.currentConfigurationPreview?.getNetPrice(howDoYouLikeYourQuoteElements) },
  ) { projectPreview ->
    val currentConfigurationPreview = projectPreview.currentConfigurationPreview
    {
      if (currentConfigurationPreview != null) {
        val earningsDistribution = currentConfigurationPreview.earningsDistribution
        val accountingTableEntriesList = earningsDistribution.allIncludingSoftware.groupBy { it.companyProfile }.map {
          EarningsTableEntries(
            companyProfile = it.key,
            configurationPreview = currentConfigurationPreview,
            earningsCompanyEntries = it.value,
          )
        }.sortedByDescending { it.companyProfile }

        accountingTableEntriesList.fastForEach { earningsTableEntries ->
          val companyInformation = companyResolver[earningsTableEntries.companyProfile]
          val earningsCompanyEntries = earningsTableEntries.relevantEarningsCompanyEntries
          val openEarningsCompanyEntries = earningsTableEntries.openEarningsCompanyEntries
          val accountedEarningsCompanyEntries = earningsTableEntries.accountedEarningsCompanyEntries

          div("mb-1") {
            div("row") {
              div("col") {
                b("form-text") { +companyInformation.simpleName }
                span("mx-1") {
                  attrs {
                    addClass("text-center")
                    addClass("align-middle")

                    if (openEarningsCompanyEntries.isEmpty()) {
                      addClass("text-success")
                    } else if (accountedEarningsCompanyEntries.isNotEmpty()) {
                      addClass("text-warning")
                    } else {
                      addClass("text-danger")
                    }
                  }

                  +accountedEarningsCompanyEntries.map { it.invoiceHistory.currentValue }.distinct().joinToString(", ") { it?.value ?: "-" }
                }
                span("ms-2") {
                  attrs {
                    addClass("text-center")
                    addClass("align-middle")

                    if (openEarningsCompanyEntries.isEmpty()) {
                      addClass("text-success")
                    } else if (accountedEarningsCompanyEntries.isNotEmpty()) {
                      addClass("text-warning")
                    } else {
                      addClass("text-danger")
                    }
                  }

                  if (openEarningsCompanyEntries.isEmpty()) {
                    faCircleCheck()
                  } else {
                    b { +"${accountedEarningsCompanyEntries.size}/${earningsCompanyEntries.size}" }
                  }
                }
              }
              div("col form-text text-end") {
                +earningsTableEntries.relevantEarnings(howDoYouLikeYourQuoteElements).format(plannerI18nConfiguration)
              }
            }
          }
        }

        hr {}

        div("row") {
          div("col my-auto") { b { +"Gesamt" } }
          div("col text-end") {
            b { +currentConfigurationPreview.getNetPrice(howDoYouLikeYourQuoteElements).format(plannerI18nConfiguration) }
          }
        }
      }
    }
  }
}
